import { action, makeObservable, observable, runInAction } from 'mobx';
import moment from 'moment-timezone';
import { NotificationManager } from '../components/notification-manager';
import { HttpMethod } from '../types';
import { saveResponseAsFile } from '../utils/download';
import { RppUploadError, uploadFileToS3 } from '../utils/rpp-assets';
import { DEFAULT_TIMEZONE } from '../utils/timezone';
import { RootStore } from './root';
import { WithNetworkConcurrency } from './with-network-concurrency';

/**
 * Worked out by the API from the publication flag and the participation
 * window: a draft is unpublished, whatever its dates.
 */
export type ContestState = 'draft' | 'upcoming' | 'running' | 'ended';

export interface ContestListItem {
    id: number;
    state: ContestState;
    startAt: string;
    endAt: string;
    timeZone: string;
    translations: {
        lang: 'en' | 'fr';
        title: string;
        urlSegment: string;
    }[];
    /** The game-list image of the contest's external game, per language. */
    images: {
        lang: 'en' | 'fr';
        url: string;
    }[];
    /** The provinces of the contest's external game, by name. */
    provinces: string[];
    /** People signed up, whether or not they entered. */
    participantCount: number;
    /** The sum of the entries' quantities: the weight of the contest in the draw. */
    chanceCount: number;
}

export type ContestConsentRecipient = 'retailer' | 'sponsor';
export type ContestConsentChannel = 'email' | 'sms';

/** A communication checkbox the sign-up form offers. */
export interface ContestConsentOption {
    recipient: ContestConsentRecipient;
    channel: ContestConsentChannel;
    /** Per language, Couche-Tard's wording and Circle K's, null when it is Couche-Tard's. */
    labels: {
        lang: 'en' | 'fr';
        labelQc: string;
        labelCa: string | null;
    }[];
}

/** Who asks the skill-testing question: the sponsor, of the winner, or the tool, before an entry. */
export type ContestSkillChallenge = 'winner' | 'in_tool';

/**
 * What the General tab edits. Instants are UTC; a null cap is no cap. The
 * provinces are those of the contest's external game.
 */
export interface ContestGeneral {
    /** Both languages. The address is the last segment of the contest's URL. */
    translations: ContestTranslation[];
    startAt: string;
    endAt: string;
    drawAt: string | null;
    timeZone: string;
    provinceIds: number[];
    minAge: number | null;
    maxEntriesPerDay: number | null;
    maxEntriesTotal: number | null;
    routeEntryQuantity: number;
    routeMaxEntriesPerDay: number | null;
    routeMaxEntriesTotal: number | null;
    retentionDays: number;
    noPurchaseCode: string | null;
    skillChallenge: ContestSkillChallenge;
    /** In form order. */
    consentOptions: ContestConsentOption[];
}

export interface ContestTranslation {
    lang: 'en' | 'fr';
    title: string;
    urlSegment: string;
}

/**
 * What the contest's external game shows in the applications: the image and
 * subtitle of its tile in the game list, and the image, description and
 * button of the screen the tile opens. Null when absent.
 */
export interface ContestGamePresentation {
    image: string | null;
    detailImage: string | null;
    subTitle: string | null;
    description: string | null;
    buttonTitle: string | null;
}

/** The kinds of file the contest public files take. */
type ContestUploadKind = 'image' | 'webImage' | 'font' | 'document';

/** One language of the Content tab. A text or image is null when absent. */
export interface ContestContentTranslation {
    lang: 'en' | 'fr';
    /** The banner at the top of the contest in the Couche-Tard app. */
    detailImageUrlQc: string | null;
    /** The banner in the Circle K app, null when it is Couche-Tard's. */
    detailImageUrlCa: string | null;
    confirmationTitle: string | null;
    confirmationBody: string | null;
    endedTitle: string | null;
    endedBody: string | null;
    endedImageUrl: string | null;
    /** The browser tab icon, null when it is the web application's. */
    faviconUrl: string | null;
    /** Stored on the contest's external game. */
    game: ContestGamePresentation;
}

/** What the Content tab edits, in both languages. */
export interface ContestContent {
    translations: ContestContentTranslation[];
}

/** A font from the predefined list, expected on the device. */
export interface ContestSystemFont {
    source: 'system';
    family: string;
    /** The font stack shown where the device lacks the family. */
    fallback: string;
}

/** A woff2 uploaded for the contest. `fallback` shows until it has loaded. */
export interface ContestUploadedFont {
    source: 'upload';
    /** The name the file is loaded under. Two files need two names. */
    family: string;
    fallback: string;
    url: string;
}

export type ContestThemeFont = ContestSystemFont | ContestUploadedFont;

/** Each colour is `#rrggbb`, each radius whole pixels such as `16px`. */
export interface ContestTheme {
    colors: {
        navBar: string;
        navBarText: string;
        pageBackground: string;
        cardBackground: string;
        bodyText: string;
        mutedText: string;
        buttonBackground: string;
        buttonText: string;
        highlightText: string;
    };
    fonts: { titles: ContestThemeFont; body: ContestThemeFont };
    radius: { cards: string; buttons: string };
}

/** How one language of a policy version is written. */
export type ContestPolicyContentType = 'html' | 'pdf' | 'external_url';

/** What one language of a policy version says: the field its type names is set. */
export interface ContestPolicyContentTranslation {
    lang: 'en' | 'fr';
    contentType: ContestPolicyContentType;
    body: string | null;
    documentUrl: string | null;
    externalUrl: string | null;
}

/** What names a policy in one language, shared by all its versions. */
export interface ContestPolicyTranslation {
    lang: 'en' | 'fr';
    urlSegment: string;
    title: string;
    /** The sign-up checkbox that accepts it, its link text in brackets. */
    acceptanceLabel: string | null;
}

/** What a policy is. A published contest has its rules and a privacy policy. */
export type ContestPolicyKind =
    | 'contest_rules'
    | 'privacy_policy'
    | 'terms_and_conditions'
    | 'other';

/** The version in force, one still to come, or one a later version replaced. */
export type ContestPolicyVersionStatus = 'in_force' | 'scheduled' | 'replaced';

/** A legal page of the contest, listed in its drawer. */
export interface ContestPolicy {
    id: number;
    kind: ContestPolicyKind;
    /** Where it applies. None means everywhere the contest is open. */
    provinceIds: number[];
    translations: ContestPolicyTranslation[];
    /** What the latest version says, in force or to come. Null before the first. */
    content: {
        version: number;
        effectiveAt: string;
        translations: ContestPolicyContentTranslation[];
    } | null;
    /** Newest first. */
    versions: {
        version: number;
        effectiveAt: string;
        status: ContestPolicyVersionStatus;
        /** The participants who accepted this version when signing up. */
        participantCount: number;
    }[];
}

/**
 * A policy as the save sends it. A change of what it says, or a date, makes
 * a new version.
 */
export interface ContestPolicySave {
    /** Null for a new policy. */
    id: number | null;
    kind: ContestPolicyKind;
    provinceIds: number[];
    translations: ContestPolicyTranslation[];
    content: {
        /** Null takes effect once saved. */
        effectiveAt: string | null;
        /** One language at least. */
        translations: ContestPolicyContentTranslation[];
    };
}

/** A signed upload to the contest public files. */
interface ContestUploadTarget {
    url: string;
    fields: Record<string, string>;
    /** Where the file is served once uploaded. */
    fileUrl: string;
}

export interface Contest extends ContestGeneral {
    id: number;
    state: ContestState;
    isActive: boolean;
    externalGameId: number;
    /** Where each language's address leads in the contest web application. */
    addresses: { lang: 'en' | 'fr'; url: string }[];
    content: ContestContent;
    /** Null while the contest keeps the web application's own look. */
    theme: ContestTheme | null;
    /** In drawer order. */
    policies: ContestPolicy[];
}

/**
 * Every tab of a contest as saved. A theme or policies left out keep what is
 * stored.
 */
export interface ContestSave {
    general: ContestGeneral;
    content: ContestContent;
    theme?: ContestTheme;
    policies?: ContestPolicySave[];
}

/** A field of the Content tab, as its form names it. */
export type ContestContentField =
    | 'detailImageUrlQc'
    | 'confirmationTitle'
    | 'confirmationBody'
    | 'endedTitle'
    | 'endedBody'
    | 'game.image'
    | 'game.detailImage'
    | 'game.subTitle'
    | 'game.description'
    | 'game.buttonTitle';

type Language = 'en' | 'fr';

/** The Content tab fields of the ended message, which has a rule of its own. */
export type ContestEndedField = Extract<
    ContestContentField,
    'endedTitle' | 'endedBody'
>;

/**
 * What each rule of the publication checklist names when it fails. A page and
 * a block are named by their key, a policy by its id. A rule with nothing to name
 * fails without detail.
 */
interface PublicationIssuesByRule {
    provinces: never;
    dates: never;
    retention: never;
    no_purchase_route: never;
    route_capped: never;
    no_purchase_code_fits: { blockKey: string };
    content: {
        lang: Language;
        field: Exclude<ContestContentField, ContestEndedField>;
    };
    ended_message: { lang: Language; field: ContestEndedField };
    home_page: never;
    blocks: { key: string };
    addresses: {
        scope: 'contest' | 'page' | 'policy';
        /** The page's key; null for the contest and a policy. */
        key: string | null;
        /** The policy's id; null for the contest and a page. */
        policyId: number | null;
        lang: Language;
    };
    rules_policy: never;
    privacy_policy: never;
    /** A province a kind of policy leaves out, by its name. */
    policies_cover_provinces: {
        kind: Exclude<ContestPolicyKind, 'other'>;
        province: string;
    };
    policies_written: { policyId: number; lang: Language };
}

export type ContestPublicationRuleName = keyof PublicationIssuesByRule;

/** One rule of the publication checklist, kept or not. */
export type ContestPublicationRule = {
    [R in ContestPublicationRuleName]: {
        rule: R;
        ok: boolean;
        issues: PublicationIssuesByRule[R][];
    };
}[ContestPublicationRuleName];

/** Who a preview starts as: someone about to sign up, or a participant already. */
export type ContestPreviewStart = 'visitor' | 'participant';

/** Opens the contest in the web application, published or not, for an hour. */
export interface ContestPreviewToken {
    token: string;
    expiresAt: string;
}

/** The person behind an entry. Their phone, email and postal code are null once anonymised. */
export interface ContestEntryParticipant {
    id: string;
    phoneNumber: string | null;
    email: string | null;
    postalCode: string | null;
    province: string;
    lang: 'en' | 'fr';
    skillPassedAt: string | null;
    anonymizedAt: string | null;
    signedUpAt: string;
    /** Where they signed up, null when the contest app did not say. */
    appTarget: ContestAppTarget | null;
    channel: ContestChannel | null;
}

/** The application a request came through: Couche-Tard's or Circle K's. */
export type ContestAppTarget = 'QC' | 'CA';

/** Where the contest app ran: a browser, or a mobile application's web view. */
export type ContestChannel = 'browser' | 'ios_app' | 'android_app';

export type ContestBlockType =
    | 'text_image'
    | 'code_entry'
    | 'no_purchase_entry'
    | 'referral'
    | 'photo_entry'
    | 'contact_form'
    | 'faq';

export type ContestEntryPath =
    | 'purchase'
    | 'no_purchase'
    | 'referral'
    | 'photo';

/** What produced the entry: a block, or a purchase made in store. */
export type ContestGrantSource = 'block' | 'in_store';

export type ContestUncountedReason =
    | 'friend_cap'
    | 'contest_total_cap'
    | 'skill_challenge';

export interface ContestEntry {
    id: string;
    createdAt: string;
    participant: ContestEntryParticipant;
    /** Null for a purchase made in store. */
    block: { key: string; type: ContestBlockType } | null;
    grantSource: ContestGrantSource;
    entryPath: ContestEntryPath;
    proofValue: string | null;
    /** The entry's weight in the draw. */
    quantity: number;
    uncountedReason: ContestUncountedReason | null;
    /** Where it was submitted, null when the contest app did not say. */
    appTarget: ContestAppTarget | null;
    channel: ContestChannel | null;
}

export interface ContestEntryDetail extends ContestEntry {
    blockRevisionId: number | null;
    /**
     * The photo the entry rests on, null without one or once anonymised.
     * `url` reads it for five minutes, and is null when the API could not sign it.
     */
    photo: { url: string | null } | null;
}

/** What anonymising a person emptied, in every contest they entered. */
export interface ContestAnonymizationReport {
    participants: number;
    messages: number;
    otpChallenges: number;
    photos: number;
}

export interface ContestEntryListResponse {
    page: number;
    perPage: number;
    total: number;
    results: ContestEntry[];
}

/**
 * The files the entries export to: `analysis` with no direct identifier,
 * `draw` with one row per participant and how to reach them.
 */
export type ContestExportProfile = 'analysis' | 'draw';

/**
 * Past this with no response, the request is taken as stuck and the export
 * buttons are freed. Once the file starts, it downloads for as long as it
 * takes, since the API writes a large contest as it reads it.
 */
const EXPORT_TIMEOUT_MS = 2 * 60 * 1000;

/** Where counted entries came from: a block's path, or a purchase made in store. */
export type ContestEntryOrigin = ContestEntryPath | 'in_store';

export type ContestPhotoReviewStatus =
    | 'pending'
    | 'approved'
    | 'manual_review'
    | 'rejected';

export interface ContestStatsParticipant {
    participantId: string;
    anonymized: boolean;
    entries: number;
    chances: number;
    /** Friends who signed up through the participant's invitation link. */
    friends: number;
}

/**
 * Counts over one contest, read from its tables when asked. An entry counts
 * when it is worth a chance. Days are the contest's, in its time zone.
 */
export interface ContestStats {
    computedAt: string;
    timeZone: string;
    /** The contest day of the request. */
    today: string;
    /** The types of the blocks the contest shows, each once. */
    blockTypes: ContestBlockType[];
    totals: {
        entries: number;
        /** What the draw weighs: the sum of the entries' quantities. */
        chances: number;
        participants: number;
        /** Participants with at least one entry. */
        enteringParticipants: number;
        /** Entering participants who entered on more than one day. */
        returningParticipants: number;
        anonymizedParticipants: number;
    };
    /** Each day from the first through today or the last, empty before the start. */
    days: {
        day: string;
        entries: number;
        newParticipants: number;
        contestViews: number;
        refusals: number;
    }[];
    /** Monday 1 to Sunday 7; an absent slot holds no entry. */
    weekHours: { weekday: number; hour: number; entries: number }[];
    origins: { origin: ContestEntryOrigin; entries: number; chances: number }[];
    entriesPerParticipant: {
        range: '1' | '2-3' | '4-9' | '10+';
        participants: number;
    }[];
    /** Page loads and clicks, not people. */
    views: {
        contestViews: number;
        pageViews: number;
        ctaClicks: number;
        shareClicks: number;
    };
    /** Codes typed in a code block, accepted or refused. */
    codes: { accepted: number; invalid: number; alreadyUsed: number };
    /** Refused attempts by reason, the most frequent first. */
    refusals: { reason: string; count: number }[];
    referral: {
        referredParticipants: number;
        referrers: number;
        countedEntries: number;
        uncountedEntries: { reason: ContestUncountedReason; count: number }[];
    };
    provinces: { province: string; participants: number }[];
    languages: { lang: 'en' | 'fr'; participants: number }[];
    appTargets: { appTarget: ContestAppTarget | null; participants: number }[];
    channels: { channel: ContestChannel | null; participants: number }[];
    regions: {
        /** The largest areas by the postal code's first three characters. */
        areas: { area: string; participants: number }[];
        participantsWithPostalCode: number;
    };
    /** One row per checkbox offered: `answered` is everyone who saw it. */
    consents: {
        recipient: ContestConsentRecipient;
        channel: ContestConsentChannel;
        answered: number;
        granted: number;
        withdrawn: number;
    }[];
    photoReviews: { status: ContestPhotoReviewStatus; count: number }[];
    messages: { received: number; failed: number };
    topByChances: ContestStatsParticipant[];
    topByFriends: ContestStatsParticipant[];
}

/** Entries query params, in the CMS's 0-based page convention. */
export type ContestEntrySearchParams = {
    page?: number;
    perPage?: number;
    /** Part of an email, a phone, a postal code or a proof, or the start of an ID. */
    search?: string;
};

export interface ContestListResponse {
    page: number;
    perPage: number;
    total: number;
    results: ContestListItem[];
}

/**
 * List query params, in the CMS's 0-based page convention. The period is two
 * calendar days, both included.
 */
export type ContestSearchParams = {
    page?: number;
    perPage?: number;
    state?: ContestState;
    from?: string;
    to?: string;
};

/**
 * The period as the instants the API compares the participation windows to:
 * from the start of the first day to the start of the day after the last,
 * in the time zone the contests are run in.
 */
export function periodInstants(
    from: string,
    to: string
): { from: string; to: string } {
    return {
        from: moment.tz(from, 'YYYY-MM-DD', DEFAULT_TIMEZONE).toISOString(),
        to: moment
            .tz(to, 'YYYY-MM-DD', DEFAULT_TIMEZONE)
            .add(1, 'day')
            .toISOString(),
    };
}

/** The rules an API refusal names, under `details.rules`. */
function rulesOf(details: unknown): ContestPublicationRule[] | null {
    const rules = (details as { rules?: unknown } | null)?.rules;
    return Array.isArray(rules) ? (rules as ContestPublicationRule[]) : null;
}

export class ContestStore extends WithNetworkConcurrency {
    @observable contests: ContestListResponse | null = null;
    @observable isFetchingList = false;
    @observable contest: Contest | null = null;
    @observable isFetchingContest = false;
    /** The last fetch of `contest` failed: there is nothing to show. */
    @observable contestFetchFailed = false;
    @observable isSaving = false;
    /** The checklist of `contest` as saved, every rule kept or not. */
    @observable publication: {
        contestId: number;
        rules: ContestPublicationRule[];
    } | null = null;
    /** The last read of `publication` failed. */
    @observable publicationFetchFailed = false;
    /**
     * The rules the last save of a published contest would have broken,
     * which got it refused. Null once a save goes through.
     */
    @observable refusedRules: ContestPublicationRule[] | null = null;
    /**
     * Files on their way to storage. A save meanwhile would send the file
     * they replace, so the contest page waits for none.
     */
    @observable pendingUploads = 0;
    /** A page of one contest's entries, `entriesContestId`'s. */
    @observable entries: ContestEntryListResponse | null = null;
    @observable entriesContestId: number | null = null;
    @observable isFetchingEntries = false;
    /** The export being downloaded, one at a time. */
    @observable exportingProfile: ContestExportProfile | null = null;
    /** The latest entries request, apart from the list's tag so neither drops the other. */
    private latestEntriesTag = Symbol();
    /** The latest checklist request: an older one answering late is dropped. */
    private latestPublicationTag = Symbol();
    /** The provinces `contests` was listed for: each application has its own. */
    private contestsProvinces: string | undefined;

    rootStore: RootStore;

    constructor(rootStore: RootStore) {
        super();
        makeObservable(this);
        this.rootStore = rootStore;
    }

    @action
    async fetchList(this: ContestStore, searchParams: ContestSearchParams) {
        const tag = this.getTag();
        this.isFetchingList = true;

        // The CMS paginates with a 0-based `page`; the API is 1-based.
        const { page, perPage, state, from, to } = searchParams;
        // An application narrows the list to its provinces, as for the game
        // list. All regions lists every contest, those without a province
        // included, so they can be given one.
        const selection = this.rootStore.applicationSelectionStore;
        const filteredProvinces = selection.appTarget
            ? selection.getProvincesFromSelectedApplication
            : undefined;
        if (filteredProvinces !== this.contestsProvinces) {
            this.contests = null;
        }
        try {
            const response = await this.rootStore.makeNetworkCall<
                ContestListResponse
            >({
                method: HttpMethod.GET,
                url: '/contests',
                params: {
                    page: (page ?? 0) + 1,
                    perPage,
                    state: state || undefined,
                    filteredProvinces,
                    ...(from && to ? periodInstants(from, to) : {}),
                },
            });

            if (!response.err && response.data && this.isLatestTag(tag)) {
                const data = response.data;
                runInAction(() => {
                    this.contests = data;
                    this.contestsProvinces = filteredProvinces;
                });
            }
        } finally {
            // An older request answering first leaves the latest one loading.
            runInAction(() => {
                if (this.isLatestTag(tag)) {
                    this.isFetchingList = false;
                }
            });
        }
    }

    @action
    async fetchContest(this: ContestStore, id: number) {
        this.isFetchingContest = true;
        this.contestFetchFailed = false;
        if (this.contest?.id !== id) {
            this.contest = null;
            this.refusedRules = null;
        }
        try {
            const response = await this.rootStore.makeNetworkCall<Contest>({
                method: HttpMethod.GET,
                url: `/contests/${id}`,
            });
            if (!response.err && response.data) {
                const data = response.data;
                runInAction(() => {
                    this.contest = data;
                });
            } else if (!response.cancelled) {
                runInAction(() => {
                    this.contestFetchFailed = true;
                });
            }
        } finally {
            runInAction(() => {
                this.isFetchingContest = false;
            });
        }
    }

    @action
    async fetchEntries(
        this: ContestStore,
        contestId: number,
        searchParams: ContestEntrySearchParams
    ) {
        const tag = Symbol();
        this.latestEntriesTag = tag;
        this.isFetchingEntries = true;
        if (this.entriesContestId !== contestId) {
            this.entries = null;
        }
        // The CMS paginates with a 0-based `page`; the API is 1-based.
        const { page, perPage, search } = searchParams;
        try {
            const response = await this.rootStore.makeNetworkCall<
                ContestEntryListResponse
            >({
                method: HttpMethod.GET,
                url: `/contests/${contestId}/entries`,
                params: {
                    page: (page ?? 0) + 1,
                    perPage,
                    // The URL parses a phone typed as digits into a number.
                    search:
                        search === undefined || search === ''
                            ? undefined
                            : String(search),
                },
            });
            if (
                !response.err &&
                response.data &&
                this.latestEntriesTag === tag
            ) {
                const data = response.data;
                runInAction(() => {
                    this.entries = data;
                    this.entriesContestId = contestId;
                });
            }
        } finally {
            // An older request answering first leaves the latest one loading.
            runInAction(() => {
                if (this.latestEntriesTag === tag) {
                    this.isFetchingEntries = false;
                }
            });
        }
    }

    /** Null when the API refused it or the request was cancelled. */
    async fetchStats(
        this: ContestStore,
        contestId: number
    ): Promise<ContestStats | null> {
        const response = await this.rootStore.makeNetworkCall<ContestStats>({
            method: HttpMethod.GET,
            url: `/contests/${contestId}/stats`,
        });
        return response.err ? null : response.data;
    }

    /** Null when the API refused it. */
    async fetchEntry(
        this: ContestStore,
        contestId: number,
        entryId: string
    ): Promise<ContestEntryDetail | null> {
        const response = await this.rootStore.makeNetworkCall<
            ContestEntryDetail
        >({
            method: HttpMethod.GET,
            url: `/contests/${contestId}/entries/${encodeURIComponent(
                entryId
            )}`,
        });
        return response.err ? null : response.data;
    }

    /** Null when the API refused it. */
    async anonymizeParticipant(
        this: ContestStore,
        contestId: number,
        participantId: string
    ): Promise<ContestAnonymizationReport | null> {
        const response = await this.rootStore.makeNetworkCall<
            ContestAnonymizationReport
        >({
            method: HttpMethod.POST,
            url: `/contests/${contestId}/participants/${encodeURIComponent(
                participantId
            )}/anonymization`,
        });
        return response.err ? null : response.data;
    }

    /**
     * Downloads one export of the contest's entries, and reports whether it
     * was saved. A refusal is shown as a notification. A file comes through
     * `fetch`, since `makeNetworkCall` hands back parsed data and no headers.
     */
    async downloadExport(
        this: ContestStore,
        contestId: number,
        profile: ContestExportProfile
    ): Promise<boolean> {
        if (this.exportingProfile !== null) {
            return false;
        }
        runInAction(() => {
            this.exportingProfile = profile;
        });
        try {
            const controller = new AbortController();
            const timeout = setTimeout(
                () =>
                    controller.abort(
                        new DOMException('No response', 'TimeoutError')
                    ),
                EXPORT_TIMEOUT_MS
            );
            let response: Response;
            try {
                response = await fetch(
                    `${
                        import.meta.env.REACT_APP_API_URL || ''
                    }/api-cms/contests/${contestId}/exports/${profile}`,
                    {
                        method: 'GET',
                        headers: this.rootStore.getHeaders(),
                        signal: controller.signal,
                    }
                );
            } finally {
                clearTimeout(timeout);
            }
            if (response.ok) {
                await saveResponseAsFile(
                    response,
                    `contest-${contestId}-${profile}.csv`
                );
                return true;
            }
            await this.handleExportRefusal(response);
            return false;
        } catch (error) {
            console.error('Contest export error:', error);
            NotificationManager.showError(
                error instanceof DOMException && error.name === 'TimeoutError'
                    ? 'The export took too long. Try again in a moment.'
                    : 'The export failed. Try again in a moment.'
            );
            return false;
        } finally {
            runInAction(() => {
                this.exportingProfile = null;
            });
        }
    }

    private async handleExportRefusal(
        this: ContestStore,
        response: Response
    ): Promise<void> {
        const body = (await response.json().catch(() => ({}))) as {
            message?: string;
        };
        if (response.status === 401) {
            this.rootStore.handleUnauthorized(body.message);
        } else {
            NotificationManager.showError(
                body.message ?? `The export failed (${response.status}).`
            );
        }
    }

    /**
     * Creates the contest with every tab at once. The new contest's id, or
     * null when the API refused it.
     */
    @action
    async createContest(
        this: ContestStore,
        { general, content, theme, policies }: ContestSave
    ): Promise<number | null> {
        this.isSaving = true;
        try {
            const response = await this.rootStore.makeNetworkCall<{
                id: number;
            }>({
                method: HttpMethod.POST,
                url: '/contests',
                data: { ...general, content, theme, policies },
            });
            return response.err ? null : response.data.id;
        } finally {
            runInAction(() => {
                this.isSaving = false;
            });
        }
    }

    /**
     * Uploads an image straight to the contest public files, and gives the
     * address it is served at. The upload is pending until a save of the
     * contest keeps it. Null when the API or the storage refused it.
     */
    async uploadImage(this: ContestStore, file: File): Promise<string | null> {
        return this.upload(file, 'image', file.type);
    }

    /**
     * Uploads an image only the web application shows, which may be an SVG,
     * as `uploadImage` does.
     */
    async uploadWebImage(
        this: ContestStore,
        file: File
    ): Promise<string | null> {
        return this.upload(file, 'webImage', file.type);
    }

    /**
     * Uploads a woff2 font as `uploadImage` does an image. The browser often
     * gives no type for a font file, so the type is the format's.
     */
    async uploadFont(this: ContestStore, file: File): Promise<string | null> {
        return this.upload(file, 'font', 'font/woff2');
    }

    /** As `uploadImage`, for a policy written as a PDF. */
    async uploadDocument(
        this: ContestStore,
        file: File
    ): Promise<string | null> {
        return this.upload(file, 'document', 'application/pdf');
    }

    private async upload(
        this: ContestStore,
        file: File,
        kind: ContestUploadKind,
        contentType: string
    ): Promise<string | null> {
        runInAction(() => {
            this.pendingUploads += 1;
        });
        try {
            return await this.storeFile(file, kind, contentType);
        } finally {
            runInAction(() => {
                this.pendingUploads -= 1;
            });
        }
    }

    private async storeFile(
        this: ContestStore,
        file: File,
        kind: ContestUploadKind,
        contentType: string
    ): Promise<string | null> {
        const response = await this.rootStore.makeNetworkCall<
            ContestUploadTarget
        >({
            method: HttpMethod.POST,
            url: '/contests/upload-target',
            data: {
                kind,
                contentType,
                contentLength: file.size,
            },
        });
        if (response.err) {
            return null;
        }
        const target = response.data;
        try {
            await uploadFileToS3(
                {
                    uploadUrl: target.url,
                    method: 'POST',
                    fields: target.fields,
                },
                file
            );
        } catch (error) {
            NotificationManager.showError(
                error instanceof RppUploadError
                    ? error.message
                    : `The ${
                          kind === 'font' ? 'font' : 'image'
                      } could not be uploaded. Try again.`
            );
            return null;
        }
        return target.fileUrl;
    }

    /** Null when the API refused one. */
    /** `province` is where a participant the preview starts as signed up. */
    async createPreview(
        this: ContestStore,
        id: number,
        start: ContestPreviewStart,
        province: string
    ): Promise<ContestPreviewToken | null> {
        const response = await this.rootStore.makeNetworkCall<
            ContestPreviewToken
        >({
            method: HttpMethod.POST,
            url: `/contests/${id}/preview`,
            data: { start, province },
        });
        return response.err ? null : response.data;
    }

    /** Runs the checklist on the contest as saved. */
    @action
    async fetchPublication(this: ContestStore, id: number) {
        const tag = Symbol();
        this.latestPublicationTag = tag;
        this.publicationFetchFailed = false;
        if (this.publication?.contestId !== id) {
            this.publication = null;
        }
        const response = await this.rootStore.makeNetworkCall<{
            rules: ContestPublicationRule[];
        }>({
            method: HttpMethod.GET,
            url: `/contests/${id}/publication`,
        });
        runInAction(() => {
            if (this.latestPublicationTag !== tag) {
                return;
            }
            if (!response.err) {
                this.publication = {
                    contestId: id,
                    rules: response.data.rules,
                };
            } else if (!response.cancelled) {
                this.publicationFetchFailed = true;
            }
        });
    }

    /**
     * Publishes the contest, then reads it back. A refusal reads the
     * checklist again, which says why.
     */
    async publishContest(this: ContestStore, id: number): Promise<boolean> {
        const response = await this.rootStore.makeNetworkCall({
            method: HttpMethod.POST,
            url: `/contests/${id}/publication`,
        });
        if (response.err) {
            if (!response.cancelled) {
                await this.fetchPublication(id);
            }
            return false;
        }
        await this.fetchContest(id);
        return true;
    }

    /** Saves every tab of the contest at once, then reads it back. */
    @action
    async updateContest(
        this: ContestStore,
        id: number,
        { general, content, theme, policies }: ContestSave
    ): Promise<boolean> {
        this.isSaving = true;
        try {
            const response = await this.rootStore.makeNetworkCall({
                method: HttpMethod.PUT,
                url: `/contests/${id}`,
                data: { ...general, content, theme, policies },
            });
            if (response.err) {
                // A refusal for another reason says nothing of the checklist.
                if (!response.cancelled) {
                    const refused =
                        response.code === 'contest_published_incomplete'
                            ? rulesOf(response.details)
                            : null;
                    runInAction(() => {
                        this.refusedRules = refused;
                    });
                }
                return false;
            }
            runInAction(() => {
                this.refusedRules = null;
            });
            await this.fetchContest(id);
            return true;
        } finally {
            runInAction(() => {
                this.isSaving = false;
            });
        }
    }
}
